/**
 * Heat Exchanger — NTU-effectiveness method or specified heat rate.
 *
 * For the NS solver, the HX couples two pipe segments (hot side, cold side).
 * Each side gets a wall heat flux q_wall applied to its pipe cells.
 *
 * Pressure drop: modeled as a fixed loss coefficient K, giving
 *   ΔP = -K × ρ × u² / 2   (always opposes flow)
 * Default K=10 is typical for plate heat exchangers at design flow.
 *
 * Effectiveness-NTU: when a UA (W/K) is specified instead of a fixed Q_rate,
 * the heat duty is found from the standard effectiveness-NTU relations,
 * selected by `hxType`:
 *   Q = ε · C_min · (T_hot_in − T_cold_in)
 * where ε = f(NTU, Cr, flow arrangement), NTU = UA / C_min, Cr = C_min / C_max.
 * Reference: Incropera & DeWitt, "Fundamentals of Heat and Mass Transfer",
 * Table 11.3 / 11.4 (effectiveness-NTU relations).
 *
 * ── ENERGY CHANNEL (N1/N7) ──────────────────────────────────────────────
 * Q_rate mode is LIVE: the network converts `energySource` to
 * Δh = Q̇/ṁ_outflow at apply time and records the APPLIED duty on
 * params._appliedQ_W (display truth). This is also how the ε-NTU duty
 * reaches the fluid in the shipped two-side pattern: coolingNetwork's
 * updateHXCoupling computes Q with the ε-NTU relations each step and writes
 * the CONSERVING pair (+Q on the cold-side HX, −Q on the hot-side HX) into
 * the two components' Q_rate — both sides now genuinely heat/cool (N7).
 *
 * For a SINGLE HX component whose one junction carries BOTH streams, the
 * per-side ε-NTU redistribution is NOT representable: the junction's
 * mass-flow-weighted mixing already merges the streams (that mixing IS the
 * heat-exchange mechanism there), the outflow pipes carry no hot/cold
 * identity, and the NET external duty of a two-stream exchanger is ZERO —
 * injecting hx_Q as an energySource would fabricate energy (CLAUDE.md
 * rule 3). hx_effectiveness / hx_NTU / hx_Cr / hx_Q therefore remain
 * READ-ONLY diagnostics on that topology, by design.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { capLossDeltaP } from './nsValve';

export type HXType = 'counter' | 'parallel' | 'cross' | 'shellAndTube';

/**
 * Effectiveness-NTU relations for the supported flow arrangements.
 *
 * @param NTU  Number of transfer units, NTU = UA / C_min  (≥ 0, dimensionless)
 * @param Cr   Capacity-rate ratio, Cr = C_min / C_max ∈ [0, 1].
 *             Cr = 0 corresponds to a phase-change side (condenser/evaporator)
 *             where one stream's capacity rate is effectively infinite.
 * @param hxType Flow arrangement.
 * @returns ε ∈ [0, 1], the heat-exchanger effectiveness.
 *
 * Limits handled explicitly:
 *  - NTU → 0  ⇒ ε → 0 for every arrangement (no transfer area).
 *  - Cr = 0   ⇒ ε = 1 − exp(−NTU) for every arrangement (phase change).
 *  - Cr = 1   ⇒ closed-form limits for counter (NTU/(1+NTU)) and parallel.
 *
 * References: Incropera & DeWitt, Fundamentals of Heat and Mass Transfer,
 * 6th ed., Table 11.3. Cross-flow (both fluids unmixed) uses the standard
 * approximation ε = 1 − exp{ (1/Cr)·NTU^0.22·[exp(−Cr·NTU^0.78) − 1] }.
 */
export function computeEffectiveness(NTU: number, Cr: number, hxType: HXType): number {
  // Guard inputs to physical bounds. Negative/NaN NTU are non-physical ⇒ 0.
  // NTU = 0 ⇒ no area ⇒ no transfer. NTU = +∞ is the physical asymptote
  // (handled by the relations below, which all converge correctly).
  if (Number.isNaN(NTU) || NTU <= 0) return 0;
  const ntu = NTU; // may be +Infinity
  const cr = Math.min(1, Math.max(0, isFinite(Cr) ? Cr : 0));

  // Phase-change limit (Cr → 0): identical for ALL arrangements.
  // ε = 1 − exp(−NTU). Use a small tolerance so the Cr=1 closed forms below
  // are only taken when genuinely balanced.
  if (cr < 1e-9) {
    return 1 - Math.exp(-ntu);
  }

  switch (hxType) {
    case 'parallel': {
      // ε = (1 − exp[−NTU(1+Cr)]) / (1 + Cr)   — valid for all Cr ∈ (0,1].
      // No singularity (denominator ≥ 1), so the Cr=1 case needs no branch.
      return (1 - Math.exp(-ntu * (1 + cr))) / (1 + cr);
    }

    case 'cross': {
      // Both fluids unmixed (approximate correlation). Reduces correctly as
      // NTU → 0 (ε → 0) and is bounded < 1 for finite NTU.
      const expo = (Math.pow(ntu, 0.22) / cr) * (Math.exp(-cr * Math.pow(ntu, 0.78)) - 1);
      const eps = 1 - Math.exp(expo);
      return Math.min(1, Math.max(0, eps));
    }

    case 'shellAndTube': {
      // 1 shell pass, 2 (or multiple-of-2) tube passes — TEMA E shell.
      // ε1 = 2 / { (1+Cr) + sqrt(1+Cr²) · [1+exp(−NTU·sqrt(1+Cr²))]
      //                                   / [1−exp(−NTU·sqrt(1+Cr²))] }
      const root = Math.sqrt(1 + cr * cr);
      const e = Math.exp(-ntu * root);
      const eps = 2 / ((1 + cr) + root * (1 + e) / (1 - e));
      return Math.min(1, Math.max(0, eps));
    }

    case 'counter':
    default: {
      // Counter-flow (existing / default). Cr = 1 has a removable singularity.
      if (Math.abs(1 - cr) < 1e-9) {
        return ntu / (1 + ntu);
      }
      const e = Math.exp(-ntu * (1 - cr));
      return (1 - e) / (1 - cr * e);
    }
  }
}

export class NSHeatExchanger implements NSComponent {
  id: string;
  type = 'heatExchanger';
  params: Record<string, any>;

  constructor(id: string, params: {
    Q_rate?: number;     // W — specified heat transfer rate (positive = heat into fluid)
    UA?: number;         // W/K — overall heat transfer coefficient × area
    hxType?: HXType;
    K_loss?: number;     // Loss coefficient for pressure drop (default 10)
    D?: number;          // m — flow passage diameter for velocity calc (default 0.2)
  }) {
    this.id = id;
    this.params = { hxType: 'counter', K_loss: 10, D: 0.2, ...params };
  }

  /**
   * Effectiveness ε for this exchanger's `hxType` at a given operating point.
   *
   * @param C_hot  Hot-side capacity rate, mdot·cp  (W/K, ≥ 0)
   * @param C_cold Cold-side capacity rate, mdot·cp (W/K, ≥ 0). Pass 0 (or
   *               Infinity) on either side to model a phase-change stream.
   * @returns ε ∈ [0, 1].
   */
  effectiveness(C_hot: number, C_cold: number): number {
    const UA = this.params.UA;
    if (!UA || UA <= 0) return 0;

    const Cmin = Math.min(C_hot, C_cold);
    const Cmax = Math.max(C_hot, C_cold);
    if (!isFinite(Cmin) || Cmin <= 0) {
      // One side has zero finite capacity rate ⇒ degenerate, no duty.
      // (A phase-change side is modelled as the LARGER rate ⇒ Cr→0, handled below.)
      return 0;
    }
    const NTU = UA / Cmin;
    const Cr = isFinite(Cmax) && Cmax > 0 ? Cmin / Cmax : 0; // Cmax=∞ ⇒ Cr=0
    return computeEffectiveness(NTU, Cr, this.params.hxType as HXType);
  }

  /**
   * Heat duty Q (W, transferred from hot to cold) from the effectiveness-NTU
   * method:  Q = ε · C_min · (T_hot_in − T_cold_in).
   * Temperatures in any consistent unit (K or °C — only the difference matters).
   */
  heatDuty(C_hot: number, C_cold: number, T_hot_in: number, T_cold_in: number): number {
    const eps = this.effectiveness(C_hot, C_cold);
    const Cmin = Math.min(C_hot, C_cold);
    if (!isFinite(Cmin) || Cmin <= 0) return 0;
    return eps * Cmin * (T_hot_in - T_cold_in);
  }

  /**
   * READ-ONLY engineering outputs for this exchanger at a given operating point.
   *
   * These are DERIVED, diagnostic quantities computed from values the model
   * already computes (UA, the two stream capacity rates, hxType). They are NOT
   * fed back into the solver and do NOT change the duty / outlet calculation —
   * the duty path (heatDuty / applyBoundaryConditions) is untouched.
   *
   * @param C_hot      Hot-side capacity rate ṁ·cp (W/K, ≥ 0). Pass Infinity for
   *                   a phase-change hot side (condensing) ⇒ Cr → 0.
   * @param C_cold     Cold-side capacity rate ṁ·cp (W/K, ≥ 0). Pass Infinity for
   *                   a phase-change cold side (evaporating) ⇒ Cr → 0.
   * @param T_hot_in   Hot-stream inlet temperature (°C or K — only ΔT matters).
   * @param T_cold_in  Cold-stream inlet temperature (°C or K — only ΔT matters).
   * @returns { eps, NTU, Cr, Q } where
   *            eps ∈ [0, 1]  effectiveness for this hxType,
   *            NTU ≥ 0       number of transfer units = UA / C_min,
   *            Cr  ∈ [0, 1]  capacity-rate ratio = C_min / C_max,
   *            Q   (W)       actual heat duty = ε·C_min·(T_hot_in − T_cold_in).
   *          Degenerate / unconfigured operating points (no UA, no finite C_min)
   *          return all-zero — physically "no transfer", never NaN.
   */
  engineeringOutputs(
    C_hot: number,
    C_cold: number,
    T_hot_in: number,
    T_cold_in: number,
  ): { eps: number; NTU: number; Cr: number; Q: number } {
    const UA = this.params.UA;
    const Cmin = Math.min(C_hot, C_cold);
    const Cmax = Math.max(C_hot, C_cold);

    // Degenerate: no area, or no finite minimum capacity rate ⇒ no transfer.
    if (!UA || UA <= 0 || !isFinite(Cmin) || Cmin <= 0) {
      return { eps: 0, NTU: 0, Cr: 0, Q: 0 };
    }

    const NTU = UA / Cmin;
    const Cr = isFinite(Cmax) && Cmax > 0 ? Cmin / Cmax : 0; // Cmax = ∞ ⇒ Cr = 0
    const eps = computeEffectiveness(NTU, Cr, this.params.hxType as HXType);
    const Q = eps * Cmin * (T_hot_in - T_cold_in);
    return { eps, NTU, Cr, Q };
  }

  applyBoundaryConditions(inlet: Junction, _outlet: Junction, _dt: number): ComponentUpdate {
    const { Q_rate, K_loss, D } = this.params;

    // Pressure drop from loss coefficient
    // K-loss pressure drop from the LIVE throughflow (C-F5 root-cause fix).
    //
    // The old code used `_currentFlow ?? 100` — a flow estimate NOTHING ever
    // updated — so every HX wrote a CONSTANT ≈ −51 kPa to its outflow cell,
    // independent of the actual flow's magnitude AND direction. Around a
    // closed loop that constant became a downhill pressure staircase (a
    // perpetual accelerator), driving the cooling loops onto the ±50 m/s
    // momentum clamp (probe: 9.8 t/s in the DN500 line ⇒ the audited "13.7×
    // design flow"). A loss must OPPOSE and SCALE with the flow:
    //     ΔP = −K·ρ·u·|u|/2,   u = ṁ_live / (ρ·A_flow)
    // ṁ_live: the solver-propagated signed throughflow (_throughMdot, same
    // channel as the N2 valve family), falling back to a caller-supplied
    // `_currentFlow` (legacy/testing hook). With NEITHER available the HX is
    // honestly inert (ΔP = 0) — it no longer invents a 100 kg/s stream.
    const rho = (typeof this.params._inletRho === 'number' && this.params._inletRho > 0)
      ? this.params._inletRho
      : 998; // kg/m³ — liquid-water fallback when no upstream density supplied
    const A_flow = Math.PI / 4 * D * D;
    const mdotRaw = this.params._throughMdot ?? this.params._currentFlow;
    const mdot_live = typeof mdotRaw === 'number' && isFinite(mdotRaw) ? mdotRaw : 0;
    const u_est = mdot_live / (rho * A_flow);
    // Rail-guarded (|ΔP| ≤ LOSS_CAP_FRACTION·P_inlet) so an oversized K/D
    // combination cannot write the outlet cell onto the 611 Pa IAPWS floor.
    const deltaP_loss = capLossDeltaP(
      -(K_loss ?? 0) * rho * u_est * Math.abs(u_est) / 2,
      inlet?.P,
    );

    // READ-ONLY engineering outputs (effectiveness-NTU diagnostics) for the
    // result metadata. Computed ONLY from already-solved/configured values —
    // they do NOT influence deltaP / deltaH / energySource / mdot, so the
    // solver's behaviour is byte-identical with or without them. Emitted only
    // when the solver has supplied the two stream-capacity context params
    // (mirrors the existing _currentFlow / _inletMdot convention); absent ⇒
    // we cannot compute Cr/NTU/Q without inventing data, so we omit them.
    const epsMetadata = this._effectivenessMetadata();

    if (Q_rate != null) {
      const metadata = { Q_hx: Q_rate, deltaP_hx: deltaP_loss, ...epsMetadata };
      // Side-channel store for the UI sync (stateSync.ts) — read-only mirror of
      // this update's metadata, never read by the solver. Mirrors the
      // NSPump._lastMetadata convention; cannot affect deltaP/deltaH/convergence.
      this.params._lastMetadata = metadata;
      return {
        deltaP: deltaP_loss,
        deltaH: 0,
        momentumSource: deltaP_loss * A_flow,
        energySource: Q_rate,
        metadata,
      };
    }

    const metadata = { deltaP_hx: deltaP_loss, ...epsMetadata };
    this.params._lastMetadata = metadata;
    return {
      deltaP: deltaP_loss,
      deltaH: 0,
      momentumSource: deltaP_loss * A_flow,
      energySource: 0,
      metadata,
    };
  }

  /**
   * Build the read-only effectiveness-NTU result-metadata fields, if the solver
   * has provided the stream-capacity context. Returns the four fields:
   *   hx_effectiveness  ε ∈ [0, 1]   (dimensionless)
   *   hx_NTU            NTU ≥ 0       (dimensionless)
   *   hx_Cr             Cr ∈ [0, 1]   (dimensionless capacity-rate ratio)
   *   hx_Q              Q (W)         actual heat duty, hot → cold positive
   * Returns {} (no fields) when the context is missing — we never invent the
   * stream capacities or inlet temperatures we'd need to compute them.
   */
  private _effectivenessMetadata(): Record<string, number> {
    const C_hot = this.params._C_hot;   // W/K — solver-supplied hot-side ṁ·cp
    const C_cold = this.params._C_cold; // W/K — solver-supplied cold-side ṁ·cp
    const T_hot_in = this.params._T_hot_in;   // °C/K — hot-stream inlet temp
    const T_cold_in = this.params._T_cold_in; // °C/K — cold-stream inlet temp

    // Require both capacities + both inlet temps to emit any field. (Infinity is
    // a valid phase-change capacity ⇒ allow it; only NaN/undefined disqualifies.)
    if (
      C_hot == null || C_cold == null ||
      T_hot_in == null || T_cold_in == null ||
      Number.isNaN(C_hot) || Number.isNaN(C_cold) ||
      Number.isNaN(T_hot_in) || Number.isNaN(T_cold_in)
    ) {
      return {};
    }

    const { eps, NTU, Cr, Q } = this.engineeringOutputs(C_hot, C_cold, T_hot_in, T_cold_in);
    return { hx_effectiveness: eps, hx_NTU: NTU, hx_Cr: Cr, hx_Q: Q };
  }
}
